import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Code2, Copy, Check, Sparkles, ArrowRight, MessageSquare, Palette } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { getEmbedOptions, createLead } from "@/lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const PRESET_COLORS = ["#2563EB", "#1E6F78", "#C0242E", "#2E6B4F", "#7C5CFF", "#B58A5A", "#0F766E", "#DB2777"];

function ClaimCard({ demo, name, accent }) {
  const [site, setSite] = useState("");
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const submit = async () => {
    if (!/.+\..+/.test(site) || !/.+@.+\..+/.test(email)) { toast.error("Enter your site URL and a valid email"); return; }
    setBusy(true);
    try {
      await createLead({ name: email.split("@")[0], email, website: site, source: "embed-claim",
        service: "Embed AI Assistant", message: `Wants to embed the "${name}" assistant (industry: ${demo}, accent ${accent}) on ${site}.` });
      setDone(true); toast.success("Got it — we'll help you set it up!");
    } catch { toast.error("Something went wrong, please try again."); } finally { setBusy(false); }
  };
  if (done) return (
    <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800 flex items-center gap-2" data-testid="embed-claim-done">
      <Check size={16} /> Thanks! We saved your details and will reach out to help you go live.
    </div>
  );
  return (
    <div className="mt-6 rounded-xl border border-slate-200 bg-blue-50/40 p-5" data-testid="embed-claim-card">
      <h3 className="font-heading font-bold text-slate-900">Want us to set it up &amp; train it on your business?</h3>
      <p className="text-sm text-slate-500 mt-1">Drop your site and email — we'll load your services, hours and FAQs so it answers accurately and captures leads.</p>
      <div className="flex flex-col sm:flex-row gap-2 mt-3">
        <input value={site} onChange={(e) => setSite(e.target.value)} placeholder="yourwebsite.com" data-testid="embed-claim-site" className="flex-1 border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand" />
        <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" data-testid="embed-claim-email" className="flex-1 border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand" />
        <button onClick={submit} disabled={busy} data-testid="embed-claim-submit" className="rounded-lg px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60 whitespace-nowrap" style={{ background: accent }}>
          {busy ? "Sending…" : "Claim my assistant"}
        </button>
      </div>
    </div>
  );
}

export default function EmbedStudio() {
  const [options, setOptions] = useState([]);
  const [demo, setDemo] = useState("restaurant");
  const [name, setName] = useState("Aria");
  const [accent, setAccent] = useState("#2563EB");
  const [title, setTitle] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0); document.title = "Embed the AI Assistant | WebsiteTech.pro";
    getEmbedOptions().then((d) => setOptions(d.options || [])).catch(() => {});
  }, []);

  const onPickDemo = (val) => {
    setDemo(val);
    const o = options.find((x) => x.demo === val);
    if (o) setName(o.name);
  };

  const snippet = useMemo(() => {
    const attrs = [
      `src="${API_BASE}/api/embed/assistant.js"`,
      `data-demo="${demo}"`,
      `data-name="${name.replace(/"/g, "")}"`,
      `data-accent="${accent}"`,
    ];
    if (title.trim()) attrs.push(`data-title="${title.replace(/"/g, "")}"`);
    return `<script ${attrs.join(" ")} defer></script>`;
  }, [demo, name, accent, title]);

  const previewSrcDoc = useMemo(() => `<!doctype html><html><head><meta name="viewport" content="width=device-width,initial-scale=1"><style>body{margin:0;font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;background:#f1f5f9;color:#475569;height:100vh;display:flex;align-items:center;justify-content:center;text-align:center;padding:20px}p{max-width:260px;font-size:14px;line-height:1.5}</style></head><body><p>This is your website.<br/>Tap the chat button in the corner to try the assistant &#8594;</p>${snippet}</body></html>`, [snippet]);

  const copy = () => {
    navigator.clipboard.writeText(snippet).then(() => { setCopied(true); toast.success("Snippet copied!"); setTimeout(() => setCopied(false), 2000); });
  };

  return (
    <>
      <Navbar />
      <main data-testid="embed-studio" className="bg-slate-50">
        <section className="pt-28 pb-10 bg-white border-b border-slate-100">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.18em] text-brand"><Code2 size={13} /> Embed · No rebuild</span>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 mt-4">Add the AI Assistant to <span className="text-brand">any website.</span></h1>
            <p className="text-slate-500 mt-4 text-lg max-w-2xl">Customize it, copy one line of code, and paste it into your existing WordPress, Wix, Shopify or hand-coded site. It works instantly.</p>
          </div>
        </section>

        <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid lg:grid-cols-2 gap-10">
          {/* Configurator */}
          <div>
            <h2 className="font-heading text-2xl font-extrabold text-slate-900">1. Customize your assistant</h2>
            <div className="mt-5 space-y-5">
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1.5">Starting knowledge (industry)</label>
                <select value={demo} onChange={(e) => onPickDemo(e.target.value)} data-testid="embed-demo-select" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand bg-white">
                  {options.map((o) => <option key={o.demo} value={o.demo}>{o.business} — {o.name} ({o.role})</option>)}
                </select>
                <p className="text-xs text-slate-400 mt-1.5">Preview uses sample knowledge for that industry. When you subscribe, we train it on <em>your</em> business.</p>
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1.5">Assistant name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} data-testid="embed-name-input" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand" />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 block mb-1.5">Header title (optional)</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Ask us anything" data-testid="embed-title-input" className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm outline-none focus:border-brand" />
              </div>
              <div>
                <label className="text-sm font-semibold text-slate-700 mb-1.5 flex items-center gap-1.5"><Palette size={14} /> Accent color</label>
                <div className="flex items-center gap-2 flex-wrap">
                  {PRESET_COLORS.map((c) => (
                    <button key={c} onClick={() => setAccent(c)} data-testid={`embed-color-${c.replace("#", "")}`} className={`h-8 w-8 rounded-full border-2 transition-transform hover:scale-110 ${accent === c ? "border-slate-900" : "border-white"}`} style={{ background: c }} aria-label={c} />
                  ))}
                  <input type="color" value={accent} onChange={(e) => setAccent(e.target.value)} className="h-8 w-10 rounded border border-slate-200 cursor-pointer" />
                </div>
              </div>
            </div>

            <h2 className="font-heading text-2xl font-extrabold text-slate-900 mt-10">2. Copy the snippet</h2>
            <div className="mt-4 rounded-xl bg-slate-900 text-slate-100 p-4 relative">
              <pre className="text-xs sm:text-sm overflow-x-auto whitespace-pre-wrap break-all font-mono" data-testid="embed-snippet">{snippet}</pre>
              <button onClick={copy} data-testid="embed-copy-btn" className="absolute top-3 right-3 inline-flex items-center gap-1.5 text-xs font-semibold bg-white/10 hover:bg-white/20 rounded-lg px-3 py-1.5 transition-colors">
                {copied ? <><Check size={13} /> Copied</> : <><Copy size={13} /> Copy</>}
              </button>
            </div>
            <p className="text-sm text-slate-500 mt-3">Paste it just before the closing <code className="bg-slate-100 px-1 rounded">&lt;/body&gt;</code> tag on your site — that's it.</p>

            <ClaimCard demo={demo} name={name} accent={accent} />
          </div>

          {/* Live preview */}
          <div>
            <h2 className="font-heading text-2xl font-extrabold text-slate-900">Live preview</h2>
            <p className="text-slate-500 text-sm mt-1">A real, working assistant — try chatting with it.</p>
            <div className="mt-4 rounded-2xl border border-slate-200 overflow-hidden card-shadow bg-white">
              <div className="flex items-center gap-1.5 px-3 py-2 border-b border-slate-100 bg-slate-50">
                <span className="h-2.5 w-2.5 rounded-full bg-red-300" /><span className="h-2.5 w-2.5 rounded-full bg-amber-300" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-300" />
                <span className="text-xs text-slate-400 ml-2">your-website.com</span>
              </div>
              <iframe key={previewSrcDoc.length + accent + demo} title="Assistant preview" srcDoc={previewSrcDoc} data-testid="embed-preview-iframe" className="w-full h-[520px] bg-slate-100" sandbox="allow-scripts allow-same-origin" />
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="rounded-3xl p-10 md:p-14 text-center bg-brand text-white">
              <Sparkles className="mx-auto mb-3" />
              <h2 className="font-heading text-3xl md:text-4xl font-extrabold tracking-tight">Want it trained on your business?</h2>
              <p className="mt-3 text-white/80 max-w-xl mx-auto">Subscribe and we'll load your services, hours, FAQs and booking so the assistant answers accurately and captures leads into your CRM.</p>
              <div className="flex flex-wrap justify-center gap-3 mt-7">
                <Link to="/tools" className="inline-flex items-center gap-2 font-semibold rounded-xl px-6 py-3.5 bg-white text-brand hover:-translate-y-0.5 transition-transform">See plans <ArrowRight size={16} /></Link>
                <Link to="/ai-chatbot" className="inline-flex items-center gap-2 font-semibold rounded-xl px-6 py-3.5 border border-white/40 hover:bg-white/10 transition-colors"><MessageSquare size={16} /> Talk to WebsiteTech AI</Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
